import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  signal,
  Type,
  viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import {
  MatChipGrid,
  MatChipInput,
  MatChipInputEvent,
  MatChipRemove,
  MatChipRow,
} from '@angular/material/chips';
import { MatOption } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FieldTypeConfig, FormlyFieldConfig } from '@ngx-formly/core';
import { FieldType, FormlyFieldProps } from '@ngx-formly/material/form-field';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EmployeeResponse } from 'net-api-lib';
import { UserReducedDto } from 'projects/cupcake/src/app/xatena-api';
import { isObservable, Observable } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { emailValidator } from '../../../email-validator';

export interface UserChipListProps extends FormlyFieldProps {
  chips: Observable<UserReducedDto[] | EmployeeResponse[]>;
  allowUserCreation?: boolean;
  labelProp: string;
  valueProp: string;
  multiple?: boolean;
  type: string;
  onAdd?: (value: string, field: FieldTypeConfig<UserChipListProps>) => void;
  onRemove?: (value: string, field: FieldTypeConfig<UserChipListProps>) => void;
}

interface User {
  username?: string | null;
  name?: string | null;
  surname?: string | null;
  function?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface FormlyUserChipListFieldConfig
  extends FormlyFieldConfig<UserChipListProps> {
  type: 'chip-list' | Type<FormlyUserChipListComponent>;
}

@Component({
  selector: 'app-formly-user-chip-list',
  templateUrl: './formly-user-chip-list.component.html',
  styleUrls: ['./formly-user-chip-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
      useValue: { overlayPanelClass: 'user-chip-panel' },
    },
  ],
  imports: [
    MatChipGrid,
    ReactiveFormsModule,
    MatChipRow,
    MatChipRemove,
    MatIcon,
    MatAutocompleteTrigger,
    MatChipInput,
    MatAutocomplete,
    MatOption,
    AsyncPipe,
    TranslateModule,
  ],
})
export class FormlyUserChipListComponent
  extends FieldType<FieldTypeConfig<UserChipListProps>>
  implements OnInit, OnDestroy
{
  subs = new SubSink();

  separatorKeysCodes: number[] = [ENTER, COMMA];

  get chipListCtrl(): FormControl<string[]> {
    return this.field.formControl;
  }

  itemControl = new FormControl();

  availableValues$!: Observable<User[]>;
  filteredValues$!: Observable<User[]>;
  allUser = signal<User[]>([]);

  readonly autocomplete = viewChild.required(MatAutocompleteTrigger);

  constructor(
    private snackBar: MatSnackBar,
    private translateService: TranslateService
  ) {
    super();
  }

  isEmployeeResponse(
    pet: UserReducedDto | EmployeeResponse
  ): pet is EmployeeResponse {
    return (pet as EmployeeResponse).oid !== undefined;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    if (this.field.props?.chips && isObservable(this.field.props.chips)) {
      this.availableValues$ = this.field.props?.chips.pipe(
        map(chips => {
          return chips.map(m => {
            if (this.isEmployeeResponse(m)) {
              return {
                username: m.username,
                name: m.name,
                surname: m.firstName,
                function: m.function,
                email: m.email,
                phone: m.phone,
              } satisfies User;
            } else {
              return {
                username: m.userId,
                name: m.firstName,
                surname: m.lastName,
                function: m.occupation,
                email: m.email,
                phone: undefined,
              } satisfies User;
            }
          });
        })
      );

      this.filteredValues$ = this.availableValues$.pipe(
        switchMap(allChips => {
          this.allUser.set(allChips);
          // Zuerst die Chips aus dem Chip List Control filtern
          return this.chipListCtrl.valueChanges.pipe(
            startWith(this.chipListCtrl.value), // Standardwert beim Start
            map(selectedChips => {
              // Chips filtern, die noch nicht ausgewählt wurden
              return allChips.filter(f =>
                (selectedChips ?? []).every(f2 => f2 !== f.username)
              );
            }),
            switchMap(filteredChips =>
              // Danach den Wert aus dem ItemControl einbeziehen
              this.itemControl.valueChanges.pipe(
                startWith(this.itemControl.value),
                map(value => {
                  if (typeof value !== 'string') return filteredChips; // Wenn der Wert kein Text ist, einfach zurückgeben

                  // Index für die Textsuche erstellen
                  const fulltextIdx = filteredChips.map(m => ({
                    userId: m.username,
                    fulltext: m.name + ' ' + m.surname + ' ' + m.username,
                  }));

                  // Suchtreffer filtern
                  const hits = fulltextIdx
                    .filter(f =>
                      f.fulltext
                        ?.toLocaleLowerCase()
                        .includes(value.toLocaleLowerCase())
                    )
                    .map(m => m.userId);

                  // Nur Treffer zurückgeben
                  return filteredChips.filter(f => hits.includes(f.username));
                })
              )
            )
          );
        })
      );
    }
  }

  add(event: MatChipInputEvent): void {
    const value = event.value || '';
    const validationError = emailValidator(value);

    let newValue = this.chipListCtrl.value ?? [];

    if (!this.props.allowUserCreation) {
      this.subs.sink = this.availableValues$.subscribe(values => {
        const exists = values.some(user => user.username === value);
        if (!exists) return;

        newValue.push(value);
        this.props.onAdd?.(value, this.field);
      });
    } else {
      if (!validationError && value) {
        if (this.props.multiple == false) newValue = [];
        newValue.push(value);
        this.props.onAdd?.(value, this.field);
      } else {
        this.snackBar.open(
          this.translateService.instant('SHARED.VALIDATION.EMAIL'),
          undefined,
          { duration: 1500 }
        );
      }
    }

    this.chipListCtrl.setValue(newValue);
    this.itemControl.setValue(null);
    this.autocomplete().closePanel();
  }

  remove(i: number): void {
    const removedValue = this.formControl.value[i];
    const value = this.formControl.value;

    this.formControl.setValue([
      ...value.slice(0, i),
      ...value.slice(i + 1, value.length),
    ]);

    this.props.onRemove?.(removedValue, this.field);
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    let newValue = this.chipListCtrl.value ?? [];

    if (newValue.find(f => f == event.option.value.username)) return;

    if (this.props.multiple == false) newValue = [];

    newValue.push(event.option.value.username);

    this.chipListCtrl.setValue(newValue);
    this.itemControl.setValue(null);

    this.props.onAdd?.(event.option.value.username, this.field);
  }

  getUserDisplayName(username: string): string {
    const user = this.allUser().find(f => f.username === username);
    return user ? `${user?.surname} ${user?.name}` : username;
  }
}
